import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import {
  cronometroLegivel,
  lerMeta,
  marcarMeta,
  registrarEstudo,
  salvarAnotacao,
  type ImagemDaAnotacao,
  type Meta,
} from "../lib/gerenciadorEstudos";

// A tela de UMA meta do cronograma — o coração do Gerenciador de Estudos.
//
// ---------------------------------------------------------------------------
// O que a tela do Bubble tinha, e o que mudou aqui (com o porquê)
// ---------------------------------------------------------------------------
// MANTIDO: o número da meta como título, o estado "concluída", as três faces do
// conteúdo (aulas / o que aprender / dicas), o cronômetro, as anotações, a
// relevância do tema, os links de aula e de caderno, e a navegação para a meta
// anterior e a seguinte.
//
// MUDOU, e cada mudança nasceu de uma medição:
//
//  1. **As aulas viraram LISTA, não um botão.** A tela de origem tem um botão
//     único "Aulas em vídeo" — e 234 das 275 metas (85%) têm mais de um link de
//     aula, até seis. O botão único não consegue servir a meta: ou abre a
//     primeira e as outras cinco ficam inalcançáveis, ou não abre nada. Aqui
//     cada aula é uma linha com o próprio link, agrupada por disciplina.
//
//  2. **"Sem link" é dito com letra.** 47 aulas do Bubble têm "." ou "x" no
//     lugar do endereço, e em 23 metas TODAS as aulas estão assim. Um botão que
//     não leva a lugar nenhum é pior que a ausência dele — e `<a href=".">`
//     recarregaria a própria página do hub.
//
//  3. **As abas mudas dizem por que estão mudas.** 92 das 271 metas com aula
//     (34%) não têm nem "o que aprender" nem dica. Ausência de linha não informa
//     nada: sem texto próprio, o aluno lê a aba vazia como tela quebrada. É a
//     mesma lição da linha "Prorrogação — não cabe" do módulo de Admissão.
//
//  4. **A relevância vira RÓTULO, não barra de 0 a 10.** Medido: a escala usada
//     vai de 6 a 10, e 249 das 275 metas (90,5%) são 7 ou 8. Numa barra de 0-10,
//     quase toda meta parece altíssima; numa de 6-10, quase toda parece baixa —
//     e as duas leituras são falsas, porque para 213 metas o valor é carimbo de
//     LOTE, não peso pensado (87 metas receberam 7 no mesmo dia, 67 receberam 8).
//     Um rótulo honesto ("relevância 8 de 10") não finge discriminação que o
//     dado não tem. Ordenar ou ponderar por ele devolveria o desempate ao acaso.
//
//  5. **O cronômetro grava no SERVIDOR, em trechos.** No Bubble ele empurra um
//     tique de ~60 s numa lista, e o resultado medido são 21 dias com mais de 24
//     horas — um deles com 2.987 horas, de cronômetro esquecido ligado. Aqui o
//     trecho é fechado ao pausar, ao trocar de meta e ao fechar a aba, com teto
//     de 4 h por chamada na RPC e de 24 h por dia na constraint.
//     Ele foi removido em 27/08 (000119, Victor: "acho meio inútil") e VOLTOU em
//     29/10 (000363, Enzo), junto com o histórico de 7.400 dias do Bubble.
//
//  6. **As imagens da anotação ficam ABAIXO dela.** O editor do Bubble deixava
//     colar imagem no meio do texto; a anotação daqui é texto simples, e cada
//     imagem do portal antigo deixou o marcador "[imagem N]" no lugar em que
//     estava. Elas são só de leitura: o hub não tem onde colar imagem nova.

const ABAS = ["aulas", "aprender", "dicas"] as const;
type Aba = (typeof ABAS)[number];

export default function MetaDoCronograma() {
  const { numero: numeroTexto } = useParams();
  const numero = Number(numeroTexto);
  const navegar = useNavigate();

  const [dados, setDados] = useState<{
    meta: Meta;
    feita: boolean;
    anotacao: string | null;
    imagens: ImagemDaAnotacao[];
    total: number;
  } | null>(null);
  const [naoExiste, setNaoExiste] = useState(false);
  const [aba, setAba] = useState<Aba>("aulas");
  const [feita, setFeita] = useState(false);
  const [erroDeMarcar, setErroDeMarcar] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(numero) || numero < 1) {
      setNaoExiste(true);
      return;
    }
    let vivo = true;
    setDados(null);
    setNaoExiste(false);
    setAba("aulas");
    lerMeta(numero).then((d) => {
      if (!vivo) return;
      if (!d) setNaoExiste(true);
      else {
        setDados(d);
        setFeita(d.feita);
      }
    });
    return () => {
      vivo = false;
    };
  }, [numero]);

  if (naoExiste) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
        <div className="surface p-6">
          <h1 className="font-display text-xl font-bold text-titulo">Esta meta não existe</h1>
          <p className="pt-2 text-sm text-cinza">
            O cronograma vai da meta 1 em diante. Use a busca do índice para ir ao número que
            você procura.
          </p>
          <Link to=".." relative="path" className="pill-cta mt-5 inline-block">
            Voltar ao cronograma
          </Link>
        </div>
      </div>
    );
  }
  if (!dados) return <Carregando />;

  const { meta, total: totalDeMetas } = dados;
  const semAula = meta.aulas.length === 0;
  const comLink = meta.aulas.filter((a) => a.url);

  async function alternarFeita() {
    const novo = !feita;
    setFeita(novo);
    setErroDeMarcar(null);
    const erro = await marcarMeta(meta.id, novo);
    if (erro) {
      // Erro de ESCRITA não derruba a tela. A peça que falhou é que informa —
      // trocar a meta inteira por uma mensagem de erro apagaria o conteúdo que o
      // aluno está lendo, que é a cicatriz do `alternarFeito` do Calendário.
      setFeita(!novo);
      setErroDeMarcar("Não conseguimos registrar agora. O conteúdo da meta continua aqui.");
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
      <Cabine
        trilha={{ para: "/ferramenta/gerenciador-de-estudos", rotulo: "Gerenciador de Estudos" }}
        rotulo={feita ? "CONCLUÍDA" : "EM ANDAMENTO"}
        titulo={`Meta ${meta.numero}`}
        descricao={
          semAula
            ? "Esta meta ainda não tem aula publicada. A equipe de conteúdo está preparando."
            : `${meta.aulas.length} ${meta.aulas.length === 1 ? "aula" : "aulas"}${
                meta.cadernos.length
                  ? ` e ${meta.cadernos.length} caderno${meta.cadernos.length === 1 ? "" : "s"} de questões`
                  : ""
              } nesta etapa do cronograma.`
        }
        fatos={
          <>
            <Cronometro metaId={meta.id} />
            {meta.relevancia !== null && (
              <TileDeFato
                rotulo="Relevância do tema"
                valor={`${meta.relevancia} de 10`}
                apoio="quanto o assunto costuma cair em prova"
              />
            )}
            {/* O segundo fato existe porque a Cabine distribui os tiles em
                `auto-fit`: com um só, ele estica pela faixa inteira do navy e
                fica um retângulo largo com quatro palavras dentro. E este é um
                fato REAL — a posição no cronograma —, não enchimento: sem ele o
                aluno não sabe onde está entre as 275. */}
            <TileDeFato
              rotulo="No cronograma"
              valor={`Meta ${meta.numero}`}
              apoio={totalDeMetas ? `de ${totalDeMetas} no total` : undefined}
            />
          </>
        }
        acao={
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={alternarFeita}
              className={feita ? "btn-contorno-navy" : "pill-cta"}
            >
              {feita ? "Desmarcar esta meta" : "Marcar como concluída"}
            </button>
            {erroDeMarcar && (
              <span role="status" className="text-sm text-laranja-claro">
                {erroDeMarcar}
              </span>
            )}
          </div>
        }
      />

      {/* --- O conteúdo, em três faces ------------------------------------- */}
      <section role="group" aria-labelledby="conteudo-da-meta" className="mt-8">
        <h2 id="conteudo-da-meta" className="sr-only">
          Conteúdo da meta {meta.numero}
        </h2>

        <div className="flex gap-1 rounded-full bg-chip p-1" role="tablist">
          {ABAS.map((a) => (
            <button
              key={a}
              type="button"
              role="tab"
              aria-selected={aba === a}
              onClick={() => setAba(a)}
              className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition ${
                aba === a ? "bg-superficie text-titulo shadow-sm" : "text-cinza hover:text-tinta"
              }`}
            >
              {a === "aulas" ? "Aulas desta meta" : a === "aprender" ? "O que aprender" : "Dica do professor"}
            </button>
          ))}
        </div>

        <div className="surface mt-4 p-5 sm:p-6">
          {aba === "aulas" && <ListaDeAulas meta={meta} />}

          {aba === "aprender" &&
            (meta.oq_aprender ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-tinta">
                {meta.oq_aprender}
              </p>
            ) : (
              <p className="text-sm text-cinza">
                Esta meta ainda não tem o resumo do que aprender. As aulas listadas na primeira
                aba trazem o conteúdo.
              </p>
            ))}

          {aba === "dicas" &&
            (meta.dica ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-tinta">{meta.dica}</p>
            ) : (
              <p className="text-sm text-cinza">
                O professor não deixou dica escrita para esta meta.
              </p>
            ))}
        </div>
      </section>

      {/* --- Os cadernos do TEC --------------------------------------------- */}
      {meta.cadernos.length > 0 && (
        <section role="group" aria-labelledby="cadernos-da-meta" className="mt-8">
          <h2 id="cadernos-da-meta" className="font-display text-lg font-bold text-titulo">
            Questões para praticar
          </h2>
          <p className="pt-1 text-sm text-cinza">
            Cadernos no TEC Concursos. É um site de terceiro e pede conta própria lá.
          </p>
          <ul className="mt-3 grid gap-2">
            {meta.cadernos.map((c) => (
              <li key={c.id}>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="surface porta flex items-center justify-between gap-4 px-4 py-3"
                >
                  <span className="min-w-0 text-sm font-semibold text-titulo">{c.titulo}</span>
                  <span className="shrink-0 text-sm text-laranja-texto">
                    Abrir no TEC
                    <span className="sr-only"> (abre em outra aba)</span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Anotacoes metaId={meta.id} inicial={dados.anotacao} imagens={dados.imagens} />

      {/* --- Navegar pelo cronograma ---------------------------------------- */}
      <nav aria-label="Navegar pelo cronograma" className="mt-8 flex items-center justify-between gap-3">
        {meta.numero > 1 ? (
          <button type="button" onClick={() => navegar(`../meta/${meta.numero - 1}`)} className="btn-contorno">
            ← Meta {meta.numero - 1}
          </button>
        ) : (
          <span />
        )}
        <Link to="/ferramenta/gerenciador-de-estudos" className="text-sm font-semibold text-link underline">
          Ver o cronograma
        </Link>
        <button type="button" onClick={() => navegar(`../meta/${meta.numero + 1}`)} className="btn-contorno">
          Meta {meta.numero + 1} →
        </button>
      </nav>

      {comLink.length === 0 && !semAula && (
        <p className="mt-6 text-center text-sm text-cinza">
          As aulas desta meta ainda não têm link publicado.
        </p>
      )}
    </div>
  );
}

/**
 * Tira o prefixo do título SÓ quando ele repete o nome da disciplina do grupo.
 *
 * O ETL traz o título ÍNTEGRO de propósito — recortar no banco erraria em 235
 * dos 713 casos, porque o prefixo usa apelido (CASP, AFO), tem erro de digitação
 * ("CONTABILDADE GERAL") e em 13 aulas aponta outra disciplina. Aqui a
 * comparação é contra o nome que veio da FK, que é a fonte confiável (zero
 * órfã): "PORTUGUÊS: Leitura e interpretação" dentro do grupo *Português* vira
 * "Leitura e interpretação"; "CASP: Conceitos Gerais" dentro de *Contabilidade
 * Pública* fica como está, porque ali o prefixo informa em vez de repetir.
 *
 * Reversível por desenho: é apresentação, e o dado continua inteiro no banco.
 */
function semOPrefixoRepetido(titulo: string, disciplina: string | null): string {
  if (!disciplina) return titulo;
  const i = titulo.indexOf(":");
  if (i < 1) return titulo;
  const chave = (s: string) =>
    s
      .normalize("NFD")
      .split("")
      // Comparação por ponto de código, sem regex: é a lição das três vezes em
      // que o shell comeu uma expressão nesta casa.
      .filter((c) => (c.codePointAt(0) ?? 0) < 0x300)
      .join("")
      .toLocaleLowerCase("pt-BR")
      .trim();
  const pre = chave(titulo.slice(0, i));
  const disc = chave(disciplina);
  // O nome da disciplina pode ter qualificador entre parênteses no catálogo
  // ("Orçamento Público (AFO)"): comparar com a parte antes dele também.
  const discCurto = disc.split("(")[0].trim();
  if (pre !== disc && pre !== discCurto) return titulo;
  const resto = titulo.slice(i + 1).trim();
  // Nunca devolver título vazio: se o prefixo era tudo, o íntegro é melhor.
  return resto.length >= 2 ? resto : titulo;
}

/**
 * As aulas, agrupadas por disciplina.
 *
 * O agrupamento é o que resolve a redundância do título sem tocar no dado: 713
 * dos 789 nomes do Bubble começam com "DISCIPLINA: tópico", e o ETL os traz
 * ÍNTEGROS de propósito. Com as aulas embaixo do nome da disciplina, o prefixo
 * que repete some da leitura (ver `semOPrefixoRepetido`) e o que informa fica.
 */
function ListaDeAulas({ meta }: { meta: Meta }) {
  if (meta.aulas.length === 0) {
    return (
      <p className="text-sm text-cinza">
        Nenhuma aula publicada nesta meta ainda. Você pode seguir para a próxima e voltar
        depois.
      </p>
    );
  }

  const grupos = new Map<string, typeof meta.aulas>();
  for (const a of meta.aulas) {
    const chave = a.disciplina ?? "Outros";
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave)!.push(a);
  }

  return (
    <div className="grid gap-5">
      {[...grupos.entries()].map(([disciplina, aulas]) => (
        <div key={disciplina}>
          <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">
            {disciplina}
          </h3>
          <ul className="mt-2 grid gap-1.5">
            {aulas.map((a) => (
              <li key={a.id}>
                {a.url ? (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-start justify-between gap-3 rounded-xl px-3 py-2 text-sm transition hover:bg-tile"
                  >
                    <span className="min-w-0 text-tinta">
                      {semOPrefixoRepetido(a.titulo, a.disciplina)}
                    </span>
                    <span className="shrink-0 font-semibold text-laranja-texto">
                      Assistir
                      <span className="sr-only"> (abre em outra aba)</span>
                    </span>
                  </a>
                ) : (
                  <div className="flex items-start justify-between gap-3 rounded-xl px-3 py-2 text-sm">
                    <span className="min-w-0 text-tinta">
                      {semOPrefixoRepetido(a.titulo, a.disciplina)}
                    </span>
                    <span className="shrink-0 text-cinza">sem link ainda</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}


/**
 * O cronômetro do estudo.
 *
 * Fecha o trecho e grava ao PAUSAR, ao sair da tela e ao fechar a aba. No Bubble
 * o mecanismo é um tique de ~60 s empurrado numa lista, e o efeito medido são 21
 * dias com mais de 24 horas — cronômetro esquecido ligado. Aqui o teto está na
 * RPC (4 h por trecho) e na constraint (24 h por dia), e a tela nunca mostra
 * milissegundos. É o componente da 000117, devolvido do `git show f0ca446^`.
 */
function Cronometro({ metaId }: { metaId: string }) {
  const [rodando, setRodando] = useState(false);
  const [decorrido, setDecorrido] = useState(0);
  const inicio = useRef<number | null>(null);
  const acumulado = useRef(0);

  const fechar = useCallback(async () => {
    if (inicio.current === null) return;
    const trecho = Date.now() - inicio.current;
    inicio.current = null;
    if (trecho < 1000) return;
    await registrarEstudo(trecho);
  }, []);

  useEffect(() => {
    if (!rodando) return;
    const t = setInterval(() => {
      if (inicio.current !== null) {
        setDecorrido(acumulado.current + (Date.now() - inicio.current));
      }
    }, 1000);
    return () => clearInterval(t);
  }, [rodando]);

  // Sair da tela ou fechar a aba fecha o trecho. Sem isto, quem navega para a
  // próxima meta perde o tempo que acabou de estudar.
  useEffect(() => {
    const aoSair = () => {
      if (inicio.current !== null) {
        const trecho = Date.now() - inicio.current;
        inicio.current = null;
        if (trecho >= 1000) void registrarEstudo(trecho);
      }
    };
    window.addEventListener("pagehide", aoSair);
    return () => {
      window.removeEventListener("pagehide", aoSair);
      aoSair();
    };
  }, [metaId]);

  return (
    <div className="tv-card p-3" data-cronometro>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-navy-mute">
        Cronômetro
      </p>
      <p className="pt-1 font-display text-xl font-bold tabular-nums leading-tight text-white">
        {cronometroLegivel(decorrido)}
      </p>
      <button
        type="button"
        onClick={async () => {
          if (rodando) {
            acumulado.current += inicio.current ? Date.now() - inicio.current : 0;
            setRodando(false);
            await fechar();
          } else {
            inicio.current = Date.now();
            setRodando(true);
          }
        }}
        className="mt-2 rounded-full bg-laranja px-4 py-1 text-xs font-bold uppercase tracking-wide text-navy-ink transition hover:brightness-105"
      >
        {rodando ? "Pausar" : "Iniciar"}
      </button>
    </div>
  );
}

/**
 * A anotação do aluno nesta meta. Uma por (aluno, meta) — texto vazio apaga.
 *
 * É texto simples, não editor rico. O editor do Bubble oferece cor, fonte,
 * tamanho, itálico e imagem colada; medido nas 1.267 anotações com texto, 90,3%
 * usam apenas o que sobrevive à conversão, e o que se perde é justamente o que a
 * doutrina recusa (cor não mora no conteúdo — amarelo escrito para fundo branco
 * fica ilegível no tema escuro). A anotação é bilhete: a mediana tem 34
 * caracteres.
 *
 * As IMAGENS coladas no Bubble ficam abaixo do texto, só para leitura (000363). E
 * a anotação que tem imagem não fica vazia: apagá-la levaria as imagens junto, e
 * elas não têm como voltar. Com o salvamento sozinho a cada 900 ms, quem seleciona
 * tudo para reescrever perderia as imagens sem perceber — então a tela não manda o
 * texto vazio, e o banco recusa se chegar (000364).
 */
function Anotacoes({
  metaId,
  inicial,
  imagens,
}: {
  metaId: string;
  inicial: string | null;
  imagens: ImagemDaAnotacao[];
}) {
  const [texto, setTexto] = useState(inicial ?? "");
  const [estado, setEstado] = useState<"parado" | "salvando" | "salvo" | "erro" | "vazia-com-imagem">("parado");
  const primeiro = useRef(true);
  const temImagem = imagens.length > 0;

  useEffect(() => {
    setTexto(inicial ?? "");
    primeiro.current = true;
  }, [inicial, metaId]);

  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return;
    }
    if (temImagem && texto.trim() === "") {
      setEstado("vazia-com-imagem");
      return;
    }
    setEstado("salvando");
    const t = setTimeout(async () => {
      const erro = await salvarAnotacao(metaId, texto);
      setEstado(erro ? "erro" : "salvo");
    }, 900);
    return () => clearTimeout(t);
  }, [texto, metaId, temImagem]);

  return (
    <section role="group" aria-labelledby="anotacoes-da-meta" className="mt-8">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="anotacoes-da-meta" className="font-display text-lg font-bold text-titulo">
          Suas anotações
        </h2>
        <span className="text-xs text-cinza" role="status">
          {estado === "salvando"
            ? "salvando…"
            : estado === "salvo"
              ? "salvo"
              : estado === "erro"
                ? "não conseguimos salvar agora"
                : estado === "vazia-com-imagem"
                  ? "não salvo"
                  : ""}
        </span>
      </div>
      <textarea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        rows={5}
        placeholder="O que você quer lembrar desta meta?"
        className="surface mt-3 w-full resize-y p-4 text-sm leading-relaxed text-tinta placeholder:text-cinza focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
      />
      {estado === "vazia-com-imagem" && (
        <p className="pt-2 text-sm text-alerta-tinta" data-aviso-vazia-com-imagem>
          Esta anotação tem imagem do portal antigo, e o texto dela não pode ficar vazio:
          apagá-la levaria as imagens junto. Escreva alguma coisa para salvar.
        </p>
      )}
      <p className="pt-2 text-xs text-cinza">
        Só você vê estas anotações. Elas ficam guardadas por meta.
      </p>

      {temImagem && (
        <div className="mt-5" data-imagens-da-anotacao>
          <h3 className="text-sm font-semibold text-titulo">Imagens desta anotação</h3>
          <p className="pt-1 text-xs text-cinza">
            Coladas no portal antigo. O marcador [imagem N] no texto mostra onde cada uma
            estava.
          </p>
          <ul className="mt-3 grid gap-3 sm:grid-cols-2">
            {imagens.map((im) => (
              <li key={im.ordem} className="surface p-3" data-imagem-ordem={im.ordem}>
                <p className="pb-2 text-xs font-semibold text-cinza">Imagem {im.ordem}</p>
                {im.url ? (
                  <a href={im.url} target="_blank" rel="noreferrer" className="block">
                    <img
                      src={im.url}
                      alt={`Imagem ${im.ordem} da sua anotação`}
                      loading="lazy"
                      className="block max-h-96 w-full rounded-lg object-contain"
                    />
                    <span className="sr-only">(abre em outra aba)</span>
                  </a>
                ) : (
                  <p className="text-sm text-cinza">Esta imagem não está disponível.</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
